import React, { Fragment, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { Navigation } from './NavigationRoot';
import { CHAT_CARD_LAYOUT } from '../aisdk';
import '../qualtrics';
import '../userpilot';
import { designTokens } from '@platform-ui/design-system';

// Add plugins below
import './plugins/testDrive';
import { getEngineeringLegacyNavFlag } from './flags';
import './i18n';
import {
  LAYOUT_NAV_ID,
  isLayoutServiceNavNestedDuplicate,
} from './utils/layoutServiceNavDuplicate';
import { isNestedNavInstance } from './utils/nestedNavInstance';
import { isCpqAppPath } from './utils/isCpqAppPath';
import { writeStorage } from '@rehooks/local-storage';
import { NavigationConfig } from './types';
import QueryClientProvider from './QueryClientProvider';
import { NavigationContextProvider } from './NavigationContext';
import { ShadowPortalProvider } from './ShadowPortalContext';
import { isRevenueNavigationPath } from './utils/isRevenueEnvironment';
import { NavigationApiIntegration } from './GlobalNav/NavigationApiIntegration';

const LazyNavigationSideEffects = React.lazy(
  () => import('./NavigationSideEffects'),
);

export const CONFIG_PROPERTY_KEY = 'config';
export const CONFIG_LOCAL_STORAGE_KEY = 'ZNav.Config';
const breadcrumbStyleRules = `
zal-breadcrumb {
  padding: 8px 16px;
}
zal-breadcrumb a {
  color: #1269ba;
}
zal-breadcrumb a:hover, zal-breadcrumb a:active {
  text-decoration: underline;
}
zal-breadcrumb a:hover {
  color: #689fd3;
}
/* Below 992px, zal-breadcrumbs causes horizontal scrolling */
.zal-crumbs.zal-crumbs {
  flex: 1;
}
.zal-breadcrumb-content.zal-breadcrumb-content {
  flex: 0;
}`;

const aiChatModalStyleRules = (() => {
  const transition = `${CHAT_CARD_LAYOUT.transitionDurationMs}ms ${CHAT_CARD_LAYOUT.transitionEasing}`;
  const cardWidthPx = CHAT_CARD_LAYOUT.cardWidthPx;
  const shellColumnWidthPx = CHAT_CARD_LAYOUT.shellColumnWidthPx;

  return `
/* Transition modals/drawers only while the AI panel animates (see ConciergeCard body attrs). */

/* TODO: Uncomment when the modals overlapping issue is fixed
body[data-z-ai-chat-state-transitioning] .MuiDialog-root {
  transition: right ${transition} !important;
}

body[data-z-ai-chat-state='open'] .MuiDialog-root {
  right: ${cardWidthPx}px;
} */

body[data-z-ai-chat-state-transitioning] .MuiDrawer-modal .MuiDrawer-paperAnchorRight {
  transition: margin-right ${transition} !important;
}

body[data-z-ai-chat-state='open'] .MuiDrawer-modal .MuiDrawer-paperAnchorRight {
  margin-right: ${shellColumnWidthPx}px;
}
`;
})();

const qualtricsStyleRules = `
.QSIFeedbackButton {
  &>button {
   width: 28px !important;

    &>div {
      padding: 10px 5px !important;
      align-items:center;
    }
  }
}`;

/** @see https://cdn.jsdelivr.net/npm/highlight.js@10.1.0/styles/a11y-light.css */
const hljsStyleRules = `
.hljs-comment,
.hljs-quote {
  color: #696969;
}

.hljs-variable,
.hljs-template-variable,
.hljs-tag,
.hljs-name,
.hljs-selector-id,
.hljs-selector-class,
.hljs-regexp,
.hljs-deletion {
  color: #d91e18;
}

.hljs-number,
.hljs-built_in,
.hljs-builtin-name,
.hljs-literal,
.hljs-type,
.hljs-params,
.hljs-meta,
.hljs-link {
  color: #aa5d00;
}

.hljs-attribute {
  color: #aa5d00;
}

.hljs-string,
.hljs-symbol,
.hljs-bullet,
.hljs-addition {
  color: #008000;
}

.hljs-title,
.hljs-section {
  color: #007faa;
}

.hljs-keyword,
.hljs-selector-tag {
  color: #7928a1;
}

.hljs {
  display: block;
  overflow-x: auto;
  background: #fefefe;
  color: #545454;
  padding: 0.5em;
}

.hljs-emphasis {
  font-style: italic;
}

.hljs-strong {
  font-weight: bold;
}

@media screen and (-ms-high-contrast: active) {
  .hljs-addition,
  .hljs-attribute,
  .hljs-built_in,
  .hljs-builtin-name,
  .hljs-bullet,
  .hljs-comment,
  .hljs-link,
  .hljs-literal,
  .hljs-meta,
  .hljs-number,
  .hljs-params,
  .hljs-string,
  .hljs-symbol,
  .hljs-type,
  .hljs-quote {
    color: highlight;
  }

  .hljs-keyword,
  .hljs-selector-tag {
    font-weight: bold;
  }
}
`;

const loadLink = (href: string) => {
  // Always load ZDS fonts
  const fontLink = document.createElement('link');
  fontLink.type = 'text/css';
  fontLink.rel = 'stylesheet';
  fontLink.crossOrigin = 'anonymous';
  fontLink.href = href;
  document.head.insertBefore(fontLink, document.head.firstChild);
};

const styleSheets = [
  'https://cdn.zuora.com/@platform-ui/design-system@2.61.0/dist/zds.css',
  'https://cdn.zuora.com/@platform-ui/zds3@0.0.43/dist/fonts.css',
];

styleSheets.forEach(loadLink);

if (window.customElements.get('z-unified-nav-v2') === undefined) {
  window.customElements.define(
    'z-unified-nav-v2',
    class ZUnifiedNavV2 extends HTMLElement {
      anjLink: HTMLLinkElement;
      mount: HTMLDivElement;
      static observedAttributes = [CONFIG_PROPERTY_KEY, 'ai-only'];
      properties: { config: NavigationConfig } = {
        config: {
          variant: null,
        },
      };
      aiOnly?: string;

      constructor() {
        super();
        // Some pages will have ZAL breadcrumbs
        this.anjLink = document.createElement('link');
        this.anjLink.rel = 'stylesheet';
        this.anjLink.crossOrigin = 'anonymous';
        this.anjLink.href =
          'https://cdn.zuora.com/@anjuna/theme@1.2.19/css/theme.css';
        this.mount = document.createElement('div');
      }

      connectedCallback() {
        const elementId = this.getAttribute('id');

        if (isNestedNavInstance(this)) {
          console.warn(
            'z-unified-nav-v2: skipping instance nested inside another nav',
            this,
          );
          /* Returning before `attachShadow` is what keeps the host's markup
           * visible: with no shadow root the `slot` attribute on our children
           * is inert, so the page content this copy wraps still renders in
           * place, under whatever the host's own `z-unified-nav-v2` rule says.
           * Nothing is written to the element — an inline style set here would
           * outlive the skip if the host ever reparented the copy. */
          return;
        }

        if (isLayoutServiceNavNestedDuplicate(elementId)) {
          console.warn(
            'z-unified-nav-v2: skipping duplicate layout-service instance',
            this,
          );
          ReactDOM.render(<Fragment />, this.mount);
          return;
        }

        /* Regardless of feature flag, zal-breadcrumbs could be used on a webpage.
         * Web components must be loaded via a script tag placed in the light DOM.
         * Likewise, Anjuna css variables are required!
         */
        const zalScript = document.createElement('script');
        zalScript.crossOrigin = 'anonymous';
        zalScript.dataset.stencilNamespace = 'zal-core';
        zalScript.src =
          'https://cdn.zuora.com/@zal/core@3.3.0-alpha.37/cjs/zal-core-bundle.cjs.js';

        // Only load Anjuna and ZAL on non-billing pages
        const isNotBillingPage = window.location.pathname.indexOf('/apps') != 0;
        if (isNotBillingPage) {
          this.appendChild(zalScript);
          // For layout service apps, always insert styles before fragment, that way
          // the css defined in the fragment wins (ex: orders UI)
          document.head.insertBefore(this.anjLink, document.head.firstChild);
        }

        /* If anjuna is not on a webpage, a couple styles need to be papered over. */
        const breadcrumbStyles = document.createElement('style');
        breadcrumbStyles.innerHTML = breadcrumbStyleRules;
        this.appendChild(breadcrumbStyles);

        /* Global rules: `body[data-z-ai-chat-state]` positions overlays when the panel is open;
         * `body[data-z-ai-chat-state-transitioning]` is set only while the panel animates so
         * modal/drawer offsets use the same transition timing as the panel.
         */
        const aiChatModalStyles = document.createElement('style');
        aiChatModalStyles.innerHTML = aiChatModalStyleRules;
        this.appendChild(aiChatModalStyles);

        /* Qualtrics offers limited styling in their GUI. Additional sytling requirements are met here. */
        const qualtricsStyles = document.createElement('style');
        qualtricsStyles.innerHTML = qualtricsStyleRules;
        this.appendChild(qualtricsStyles);

        const params = new URLSearchParams(window.location.search);

        const escapeHatchKey = 'ZUnifiedNavFlagOverride';
        const primaryEscapeHatchValue = params.get(escapeHatchKey);
        const secondaryEscapeHatchValue =
          window.localStorage.getItem(escapeHatchKey);
        const hasUsefulEscapeHatchValue =
          primaryEscapeHatchValue !== null ||
          secondaryEscapeHatchValue !== null;
        const useLegacyNavigation = (() => {
          const isBillingPage = window.location.pathname.indexOf('/apps') === 0;
          /*
           * If the web component integration is being used on a billing page, we *must* show the restyle.
           * The JSP implementation will be turned off, and ZAL is not usable inside of Billing.
           */
          if (isBillingPage) {
            return false;
          }

          return hasUsefulEscapeHatchValue
            ? primaryEscapeHatchValue === 'false' ||
                secondaryEscapeHatchValue === 'false'
            : getEngineeringLegacyNavFlag();
        })();

        const shadow = this.attachShadow({ mode: 'open' });
        // Use legacy ZAL nav when appropriate; this flow won't be reached by billing pages (/apps)
        // since Billing has its own flag and it's own older navigation implementation
        if (useLegacyNavigation) {
          shadow.innerHTML = `
          <style>
            zal-app {
              font-family: ${designTokens.fonts.fontFamily};
              font-size: .875rem;
              font-weight: 400;
              line-height: 1.5;
            }
            a {
              text-decoration: none;
              color: var(--anj-highlight);
            }
            .search-container.sc-zal-entity-switcher {
              width: auto !important;
            }
            zal-app zal-container {
              box-sizing: border-box;
            }
            /* https://stackoverflow.com/questions/35820429/main-element-not-working-in-internet-explorer-11 */
            .zal-app-main {
              display: block;
            }
          </style>
          <zal-app>
            <zal-nav
              slot="nav"
              endpoint="/platform/api/menus"
              load-state-endpoint="/platform/api/state"
              save-state-endpoint="/platform/api/navigation-state"
            >
              <zal-entity-switcher endpoint="/platform/api/identity"></zal-entity-switcher>
            </zal-nav>
            <slot slot="breadcrumbs" name="breadcrumbContent"></slot>
            <slot slot="mainContent" name="pageContent"></slot>
          </zal-app>
        `;
          return;
        }

        const shadowStyles = document.createElement('style');
        shadow.appendChild(shadowStyles);
        const emotionMount = document.createElement('div');
        emotionMount.setAttribute('data-about', 'emotion-mount');

        const popoverPortal = document.createElement('div');
        popoverPortal.setAttribute('data-popover-portal', '');

        shadow.appendChild(emotionMount);
        shadow.appendChild(popoverPortal);
        shadow.appendChild(this.mount);

        const hljsStyles = document.createElement('style');
        hljsStyles.innerHTML = hljsStyleRules;
        shadow.appendChild(hljsStyles);

        const isAiOnly = this.aiOnly === 'true';

        /**
         * When the upstream `z-unified-nav-v2` fires `navRendered` before this
         * layout-service instance (`id` === `LAYOUT_NAV_ID`), re-render with
         * `suppressNestedDuplicateChrome` so duplicate chrome is hidden. Initial
         * render defers to `isLayoutServiceNavNestedDuplicate(id)` inside
         * `Navigation` when two nav elements are present.
         */
        document.addEventListener('navRendered', (event) => {
          const renderedId = (event as CustomEvent).detail.id;

          if (renderedId !== LAYOUT_NAV_ID && elementId === LAYOUT_NAV_ID) {
            console.info(
              'z-unified-nav-v2: rendering layout-service nav',
              this,
            );

            ReactDOM.render(
              <ShadowPortalProvider value={popoverPortal}>
                <QueryClientProvider>
                  {/* Match this branch's empty NavigationContextProvider overrides:
                      the layout-service nav carries no variant config, so let it
                      fall back to location-based detection. */}
                  <NavigationApiIntegration />
                  {isAiOnly ? null : (
                    <NavigationContextProvider overrides={{}}>
                      <Suspense fallback={<React.Fragment />}>
                        <LazyNavigationSideEffects />
                      </Suspense>
                    </NavigationContextProvider>
                  )}
                  <Navigation
                    suppressNestedDuplicateChrome
                    emotionMount={emotionMount}
                    id={elementId}
                    aiOnly={isAiOnly}
                  />
                </QueryClientProvider>
              </ShadowPortalProvider>,
              this.mount,
            );
          }
        });

        ReactDOM.render(
          <ShadowPortalProvider value={popoverPortal}>
            <QueryClientProvider>
              <NavigationApiIntegration
                variant={this.properties.config.variant || undefined}
              />
              {isAiOnly ? null : (
                <NavigationContextProvider
                  overrides={{
                    variant: this.properties.config.variant || undefined,
                  }}
                >
                  <Suspense fallback={<React.Fragment />}>
                    <LazyNavigationSideEffects />
                  </Suspense>
                </NavigationContextProvider>
              )}

              <Navigation
                emotionMount={emotionMount}
                id={elementId}
                overrides={{
                  variant: this.properties.config.variant || undefined,
                }}
                aiOnly={isAiOnly}
              />
            </QueryClientProvider>
          </ShadowPortalProvider>,
          this.mount,
        );
      }

      attributeChangedCallback(name, oldValue, newValue) {
        /* A nested copy never renders, and `config` here would otherwise
         * overwrite the shared `ZNav.Config` storage key that the real nav
         * reads from. */
        if (isNestedNavInstance(this)) {
          return;
        }

        if (newValue) {
          if (name === CONFIG_PROPERTY_KEY) {
            try {
              this.properties[name] = JSON.parse(newValue);
              writeStorage(CONFIG_LOCAL_STORAGE_KEY, this.properties[name]);
            } catch (e) {
              console.error('Error parsing config JSON: ', e);
            }
          }
          if (name === 'ai-only') {
            this.aiOnly = newValue;
          }
        }
      }

      disconnectedCallback() {
        if (window.location.pathname.indexOf('/apps') != 0) {
          /* `connectedCallback` returns before inserting the link whenever the
           * instance is skipped, so it may never have reached the head. An
           * unguarded `removeChild` throws there, and the throw would take the
           * unmount below with it. */
          if (document.head.contains(this.anjLink)) {
            document.head.removeChild(this.anjLink);
          }
        }
        ReactDOM.unmountComponentAtNode(this.mount);
      }
    },
  );
}

const isOneId = window.location.hostname.startsWith('one');
const isEmbedded = window.location.hostname.startsWith('embedded');
const isRevenue = isRevenueNavigationPath(window.location.pathname);
const isCPQPath = isCpqAppPath(window.location.pathname);

if ((isOneId && !isRevenue) || (isEmbedded && isCPQPath)) {
  // Auto-open chat on embedded domains when ai-only is in the URL
  if (isEmbedded && window.location.href.includes('ai-only')) {
    sessionStorage.setItem('ZNav.ConciergeIsChatOpen', JSON.stringify(true));
  }
  const aiOnlyNav = document.createElement('z-unified-nav-v2');
  aiOnlyNav.setAttribute('ai-only', 'true');
  document.body.appendChild(aiOnlyNav);
}
